<template>
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="show"
        class="fo-backdrop fixed-top d-flex justify-content-center align-items-center"
        role="dialog"
        aria-modal="true"
        aria-live="assertive"
        @keydown.esc="handleEsc"
      >
        <div
          class="fo-card card shadow-lg text-center"
          :class="[variantClass, slug]"
          tabindex="0"
          ref="cardRef"
        >
          <div class="card-body p-4">
            <div class="d-flex flex-column align-items-center gap-2">
              <span class="fo-icon material-symbols-rounded" v-if="iconName">
                {{ iconName }}
              </span>
              <h2 class="h5 mb-1">
                <slot name="title">
                  {{ titleText }}
                </slot>
              </h2>
              <p class="mb-0">
                <slot>
                  {{ message }}
                </slot>
              </p>
            </div>

            <!-- BOTONES -->
            <div
              class="d-flex justify-content-center gap-2 mt-4"
              v-if="mode === 'alert' && closable"
            >
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="close"
              >
                Cerrar
              </button>
            </div>

            <div
              class="d-flex justify-content-center gap-2 mt-4"
              v-else-if="mode === 'confirm'"
            >
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="onCancel"
              >
                {{ cancelLabel }}
              </button>
              <button
                type="button"
                class="btn btn-success btn-sm"
                @click="onConfirm"
              >
                {{ okLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, computed } from "vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  status: { type: String, default: "success" }, // 'success' | 'error' | 'info'
  message: { type: String, default: "" },
  slug: { type: String, default: "" },
  duration: { type: Number, default: 2500 },
  closable: { type: Boolean, default: true },
  title: { type: String, default: "" },
  blockBackdropClick: { type: Boolean, default: false },

  // 👇 nuevo
  mode: { type: String, default: "alert" }, // 'alert' | 'confirm'
  okLabel: { type: String, default: "Aceptar" },
  cancelLabel: { type: String, default: "Cancelar" },
});

const emit = defineEmits([
  "update:modelValue",
  "after-hide",
  "show",
  "hide",
  "confirm", // 👈 nuevo
  "cancel", // 👈 nuevo
]);

const show = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const cardRef = ref(null);
let timer = null;

const iconName = computed(() => {
  if (props.status === "success") return "check_circle";
  if (props.status === "error") return "error";
  return "info";
});

const titleText = computed(() => {
  if (props.title) return props.title;
  if (props.status === "success") return "¡Operación exitosa!";
  if (props.status === "error") return "Ocurrió un error";
  return "Aviso";
});

const variantClass = computed(() => {
  switch (props.status) {
    case "success":
      return "bg-success-subtle border-success text-success-emphasis";
    case "error":
      return "bg-danger-subtle border-danger text-danger-emphasis";
    default:
      return "bg-info-subtle border-info text-info-emphasis";
  }
});

function startTimer() {
  clearTimer();
  // ⛔ si es modo confirm, NO se autocierra
  if (props.mode === "confirm") return;

  if (props.duration && props.duration > 0) {
    timer = setTimeout(() => close(), props.duration);
  }
}
function clearTimer() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function close() {
  if (!show.value) return;
  show.value = false;
  emit("hide");
  setTimeout(() => emit("after-hide"), 200);
}

function onConfirm() {
  emit("confirm");
  close();
}

function onCancel() {
  emit("cancel");
  close();
}

function handleEsc() {
  if (!props.closable) return;
  // En modo confirm, ESC = cancelar
  if (props.mode === "confirm") {
    onCancel();
  } else {
    close();
  }
}

function onBackdropClick(e) {
  if (!props.closable || props.blockBackdropClick) return;
  if (e.target.classList.contains("fo-backdrop")) {
    // En confirm, tratamos el click afuera como "cancelar"
    if (props.mode === "confirm") {
      onCancel();
    } else {
      close();
    }
  }
}

watch(
  () => show.value,
  (val) => {
    clearTimer();

    if (val) {
      emit("show");
      startTimer();
      requestAnimationFrame(() => {
        cardRef.value?.focus?.();
      });

      document.body.style.overflow = "hidden"; // 👈 bloquea scroll
    } else {
      document.body.style.overflow = ""; // 👈 lo restaura
    }
  }
);

onMounted(() => {
  if (show.value) {
    startTimer();
    document.addEventListener("click", onBackdropClick, { capture: true });
    document.body.style.overflow = "hidden";
  }
});
onUnmounted(() => {
  clearTimer();
  document.removeEventListener("click", onBackdropClick, { capture: true });
  document.body.style.overflow = "";
});
</script>

<style scoped>
.fo-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 9999;

  display: flex;
  justify-content: center;
  align-items: center;
}

.fo-card {
  width: 380px;
  max-width: 90%;
  background: white;
  border-radius: 12px;
  z-index: 10000;
}

</style>
